import _ from 'lodash';
import { notifyOnError } from '~/rage/decorators';
import { IObservable, Subject } from '~/rage/utils';
import {
	ClientError,
	ClientErrorCode,
	HandlingMode,
	IClientConfigs,
	IHandlingTuningPreset,
	IHandlingTuningPresetReq,
	TechTuningSection,
	VehicleHandling,
	VehicleHandlingWithModes,
	deserializeHandling,
	serializeHandling,
	serializeHandlingWithModes,
} from '~/shared';
import { getCurrentDateString } from '~/utils/date';
import { BackendService } from '../BackendService';
import { GameConfigService } from '../GameConfigService';
import { IPlayerService } from '../PlayerService';
import { IRpcService } from '../RpcService';
import { VarsService } from '../VarsService';
import { IVehicleService } from '../VehicleService';
import {
	IHandlingTuningService,
	TuningParameterConfig,
	TuningSectionConfig,
} from './handlingTuningServiceTypes';

export class HandlingTuningService implements IHandlingTuningService {
	private handling = new Subject<VehicleHandling>({});
	private handlingByMode = {
		[HandlingMode.Comfort]: new Subject<VehicleHandling>({}),
		[HandlingMode.Drift]: new Subject<VehicleHandling>({}),
		[HandlingMode.Sport]: new Subject<VehicleHandling>({}),
	};
	private presets = new Subject<IHandlingTuningPreset[]>([]);
	private sections = new Subject<TechTuningSection[]>([]);
	private allSections = new Subject<TuningSectionConfig[]>([]);

	private handlingTuning = new Subject<TuningParameterConfig[]>([]);

	private syncHandling(
		mode: HandlingMode,
		handling: VehicleHandling,
		shouldClamp: boolean = true,
	): void {
		const value = shouldClamp ? this.clampHandling(handling) : handling;
		this.handlingByMode[mode].next(value);
		if (this.vehicles.getLocalVehicleHandlingMode().getValue() === mode) {
			this.handling.next(value);
		}
	}

	constructor(
		private readonly rpc: IRpcService,
		private readonly backend: BackendService,
		private readonly vehicles: IVehicleService,
		private readonly player: IPlayerService,
		gameConfig: GameConfigService,
		vars: VarsService,
	) {
		gameConfig.getConfigs().subscribe((config: Partial<IClientConfigs>) => {
			if (!config) {
				return;
			}

			this.handlingTuning.next(config.vehicleTechTuning?.parameters ?? []);
			this.allSections.next(config.vehicleTechTuning?.handlingSections ?? []);
		});

		backend.addEventHandler(
			'techTuning:updatePresets',
			(presets: IHandlingTuningPreset[]) => {
				this.presets.next(
					presets.map((e) => {
						return {
							...e,
							handling: this.clampHandling(deserializeHandling(e.handling)),
						};
					}),
				);
			},
		);

		vars
			.get<TechTuningSection[]>('techTuning:sections', [])
			.subscribe((sections) => {
				this.sections.next(sections);
			});

		vars
			.get<TuningParameterConfig[]>('techTuning:properties', [])
			.subscribe((properties) => {
				this.handlingTuning.next(properties);
			});

		this.vehicles.getLocalVehicleHandlingMode().subscribe((mode) => {
			this.handling.next(this.handlingByMode[mode].getValue());
		});
	}

	public getParameters(): IObservable<
		IClientConfigs['vehicleTechTuning']['parameters']
	> {
		return this.handlingTuning;
	}

	public getSections(): IObservable<TechTuningSection[]> {
		return this.sections;
	}

	public getAllSections(): IObservable<TuningSectionConfig[]> {
		return this.allSections;
	}

	public getHandling(): IObservable<VehicleHandling> {
		return this.handling;
	}

	private clampHandling(handling: VehicleHandling): VehicleHandling {
		const vehicleHandling: VehicleHandling = _.cloneDeep(handling);
		const handlingTuning = this.handlingTuning.getValue();

		Object.entries(vehicleHandling).forEach(([property, value]) => {
			if (typeof value === 'number') {
				const foundProperty = handlingTuning.find(
					(e) => e.property === property,
				);
				if (foundProperty) {
					const roundAmount = (
						foundProperty.step.toString().split('.')[1] || ''
					).length;

					vehicleHandling[property] = _.round(value, roundAmount);
				}
			}
		});

		return vehicleHandling;
	}

	@notifyOnError
	public async fetchValues(): Promise<void> {
		Object.values(HandlingMode).forEach(async (mode) => {
			const stockHandling = await this.vehicles.getLocalVehicleStockHandling();
			let handling = await this.vehicles.getLocalVehicleHandling(mode);

			if (handling) {
				handling = _.merge(
					this.clampHandling(stockHandling),
					this.clampHandling(handling),
				);
			} else {
				handling = this.clampHandling(stockHandling);
			}

			this.syncHandling(mode, handling!);
		});
	}

	public setHandling(handling: VehicleHandling): void {
		const mode = this.vehicles.getLocalVehicleHandlingMode().getValue();
		this.syncHandling(mode, handling);
	}

	@notifyOnError
	public async applyHandling(): Promise<void> {
		const mode = this.vehicles.getLocalVehicleHandlingMode().getValue();
		const handling = this.handling.getValue();
		const vehicleId = this.vehicles.getLocalOwnedVehicleId().getValue();

		if (!handling || !vehicleId) {
			return;
		}

		const clampedHandling = this.clampHandling(handling);

		await this.backend.invoke<void>('techTuning:setHandling', {
			vehicleId,
			handling: serializeHandlingWithModes({
				[mode]: clampedHandling,
			} as VehicleHandlingWithModes),
		});
	}

	@notifyOnError
	public async setHandlingMode(mode: HandlingMode): Promise<void> {
		const vehicleId = this.vehicles.getLocalOwnedVehicleId().getValue();

		if (!vehicleId) {
			return;
		}

		await this.backend.invoke<void>('techTuning:setHandlingMode', {
			vehicleId,
			mode,
		});
	}

	@notifyOnError
	public async resetAllSectionHandling(): Promise<void> {
		const stockHandling = await this.vehicles.getLocalVehicleStockHandling();
		const mode = this.vehicles.getLocalVehicleHandlingMode().getValue();
		this.syncHandling(mode, stockHandling);
	}

	@notifyOnError
	public async resetSectionHandling(section: string): Promise<void> {
		const mode = this.vehicles.getLocalVehicleHandlingMode().getValue();
		const handling = { ...this.handling.getValue() };
		const stockHandling = await this.vehicles.getLocalVehicleStockHandling();
		const handlingTuning = this.handlingTuning.getValue();
		handlingTuning
			.filter((e) => e.section === section)
			.forEach((e) => {
				handling[e.property] = stockHandling[e.property];
			});

		this.syncHandling(mode, handling);
	}

	public getPresets(): IObservable<IHandlingTuningPreset[]> {
		return this.presets;
	}

	@notifyOnError
	public async resetPropertyHandling(property: string): Promise<void> {
		const mode = this.vehicles.getLocalVehicleHandlingMode().getValue();
		const handling = { ...this.handling.getValue() };
		const stockHandling = await this.vehicles.getLocalVehicleStockHandling();

		handling[property] = stockHandling[property];

		this.syncHandling(mode, handling);
	}

	@notifyOnError
	public async addPreset(name: string, info: string = ''): Promise<void> {
		const mode = this.vehicles.getLocalVehicleHandlingMode().getValue();
		const handling = this.handling.getValue();
		const playerName = this.player.getNickname().getValue();
		const vehicleId = this.vehicles.getLocalOwnedVehicleId().getValue();
		if (vehicleId === null) {
			throw new ClientError(ClientErrorCode.NotInVehicle);
		}

		const preset: IHandlingTuningPresetReq = {
			name,
			info,
			mode,
			handling: serializeHandling(handling),
			favorite: false,
			author: playerName,
			creationDate: getCurrentDateString(),
			vehicleId,
		};

		await this.backend.invoke<void>('techTuning:addPreset', preset);
	}

	public applyPreset(presetIndex: number): void {
		const preset = this.presets.getValue()[presetIndex] as
			| IHandlingTuningPreset
			| undefined;
		if (preset !== undefined) {
			const { handling } = preset;
			this.setHandling(handling);
		}
	}

	@notifyOnError
	public async removePresetByIndex(presetIndex: number): Promise<void> {
		return await this.backend.invoke<void>('techTuning:removePresetByIndex', {
			index: presetIndex,
		});
	}

	@notifyOnError
	public async setPresetFavorite(
		presetIndex: number,
		favorite: boolean,
	): Promise<void> {
		return await this.backend.invoke<void>('techTuning:setPresetFavorite', {
			index: presetIndex,
			favorite,
		});
	}

	public async testDrive(): Promise<void> {
		return await this.rpc.invokeServer('techTuning:testDrive');
	}

	public async exitMenu(): Promise<void> {
		return await this.rpc.invokeClient<void>('techTuning:exitMenu');
	}
}
